import type { ReactNode } from 'react';

import { LoaderCircle, MessageSquareDashed } from '@parrot-software/design-system/icons';

import { StepCard } from '@identity/presentation/components/base/StepCard';

export type ChannelAwaitingCardProps = {
  /** The actions of the wait: resend, and the way back to the capture view. */
  children: ReactNode;
  /** What the card says under the title, usually a `<Trans>` with the destination in bold. */
  description: ReactNode;
  /** Inline error of the last send, **already translated**. */
  errorMessage?: string;
  /** True after enough failures in a row to be worth telling the user about. */
  hasPersistentNetworkError: boolean;
  /** True once the wait gave up. It swaps the spinner for a static icon. */
  isTimedOut: boolean;
  /** The text of the connection notice, already translated. */
  networkNotice: string;
  title: string;
};

/**
 * The shell the two waits of this domain share.
 *
 * It knows nothing about polling and nothing about channels: it draws the icon for the state, the
 * header, the connection notice, the inline error and whatever actions it is given. It is the only
 * part of the two waits that would otherwise be copied word for word.
 *
 * @param {ChannelAwaitingCardProps} props - Component properties
 */
export const ChannelAwaitingCard = ({
  children,
  description,
  errorMessage,
  hasPersistentNetworkError,
  isTimedOut,
  networkNotice,
  title,
}: ChannelAwaitingCardProps) => (
  <StepCard
    description={description}
    icon={
      <span className="flex size-8 items-center justify-center rounded-full bg-primary/10">
        {isTimedOut ? (
          <MessageSquareDashed aria-hidden className="size-4 text-primary" data-testid="channel-awaiting-timed-out" />
        ) : (
          <LoaderCircle
            aria-hidden
            className="size-4 animate-spin text-primary"
            data-testid="channel-awaiting-spinner"
          />
        )}
      </span>
    }
    title={title}
  >
    <div className="flex flex-col gap-6 font-sans">
      {hasPersistentNetworkError && (
        <p className="text-sm text-muted-foreground" data-testid="channel-awaiting-network-notice">
          {networkNotice}
        </p>
      )}

      {errorMessage && (
        <p className="text-xs text-destructive" data-testid="channel-awaiting-error">
          {errorMessage}
        </p>
      )}

      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:gap-2">{children}</div>
    </div>
  </StepCard>
);
